import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Activity, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [closedNotice, setClosedNotice] = useState(null);

  // Dismiss all toasts on mount (clears logout messages)
  useEffect(() => {
    toast.dismiss();
  }, []);

  // Caps Lock detection
  const handleKeyPress = (e) => {
    const capsLockIsOn = e.getModifierState && e.getModifierState('CapsLock');
    setCapsLockOn(capsLockIsOn);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    setClosedNotice(null);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      const { hospital_name, username: user, role, first_login, status, suspension_message, specialty } = response.data;

      localStorage.setItem('token', 'session');
      localStorage.setItem('hospital_name', hospital_name);
      localStorage.setItem('username', user);
      localStorage.setItem('role', role);
      if (specialty) localStorage.setItem('specialty', specialty); else localStorage.removeItem('specialty');

      // If the account is suspended, send them to the warning page only
      if (status === 'Suspended') {
        localStorage.setItem('user_status', 'Suspended');
        if (suspension_message) {
          localStorage.setItem('suspension_message', suspension_message);
        }
        navigate('/suspended');
        return;
      }
      // Clear any stale suspension flags from a previous session
      localStorage.removeItem('user_status');
      localStorage.removeItem('suspension_message');

      if (first_login) {
        // Redirect to password setup (temp password passed via router state, never persisted)
        navigate('/setup-password', { state: { tempPassword: password } });
      } else {
        toast.success('Login successful!');
        navigate(['Consultant', 'Gynaecologist', 'Specialist', 'Pharmacist', 'Lab', 'Radiology'].includes(role) ? '/workflow' : '/dashboard');
      }
    } catch (error) {
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Invalid username or password. Please check your credentials and try again.';
            break;
          case 403: {
            const detail = error.response.data?.detail;
            if (detail && typeof detail === 'object' && detail.code === 'HOSPITAL_CLOSED') {
              setClosedNotice(detail);
              setLoading(false);
              return;
            }
            errorMessage = (typeof detail === 'string' && detail) || 'Your account has been suspended. Please contact the administrator.';
            break;
          }
          case 404:
            errorMessage = 'User not found. Please check your username.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later or contact support.';
            break;
          default:
            errorMessage = error.response.data?.detail || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      }
      
      setLoginError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-green-100">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-2xl shadow-lg">
              <Activity className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Acacia Insurance</h1>
          <p className="text-center text-gray-600 mb-8">Claims Management System</p>

          {closedNotice && (
            <div className="mb-6 p-5 bg-gradient-to-br from-green-50 to-white border border-green-300 rounded-xl" data-testid="hospital-closed-notice">
              <div className="flex items-start gap-3">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-2 rounded-lg flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-green-800 mb-1">
                    {closedNotice.open && closedNotice.close ? 'Outside Working Hours' : 'Temporarily Closed'}
                  </h3>
                  <p className="text-sm text-green-900 leading-relaxed">{closedNotice.message}</p>
                  {closedNotice.open && closedNotice.close && (
                    <p className="text-xs text-green-700 mt-2 font-semibold">
                      Working hours: {closedNotice.open} – {closedNotice.close} (CAT)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-700 font-medium">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                className="h-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={handleKeyPress}
                  placeholder="Enter your password"
                  required
                  className={`h-11 border-gray-300 focus:border-green-500 focus:ring-green-500 ${capsLockOn ? 'border-yellow-400' : ''}`}
                />
                {capsLockOn && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded flex items-center gap-1">
                      <span className="font-semibold">⚠</span> Caps Lock is ON
                    </div>
                  </div>
                )}
              </div>
              {capsLockOn && (
                <p className="text-xs text-yellow-600 flex items-center gap-1">
                  <span className="font-semibold">⚠</span> Caps Lock is ON - Your password may be incorrect
                </p>
              )}
            </div>

            {loginError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 flex items-start gap-2">
                  <span className="text-red-600 font-bold">✖</span>
                  <span>{loginError}</span>
                </p>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg shadow-md transition-all duration-200"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;